<template>
  <Container
    id="heading"
  >
    <HeroCard
      :heading="t('jc.view.graphicArtist.hero.title')"
      :paragraph="t('jc.view.graphicArtist.hero.text')"
      :image="{
        src: banner,
        alt: t('jc.view.graphicArtist.hero.banner.alt'),
      }"
      :float="{
        src: float,
        alt: t('jc.view.graphicArtist.hero.float.alt'),
      }"
      :label="t('jc.view.graphicArtist.hero.button.label')"
      :icon="{ prefix: 'fab', iconName: 'discord' }"
      :to="config.public.discordParticipateUrl"
    />
  </Container>

  <Container
    id="our-offer"
  >
    <ContentCard
      :heading="t('jc.view.graphicArtist.ourOffer.title')"
      :paragraph="t('jc.view.graphicArtist.ourOffer.text')"
    >
      <IconWidget
        :items="ourOfferHighlightItems"
        class="my-12"
      />
      <IconList
        :items="ourOfferItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="our-conditions"
  >
    <ContentCard
      :heading="t('jc.view.graphicArtist.ourConditions.title')"
      :paragraph="t('jc.view.graphicArtist.ourConditions.text')"
    >
      <IconList
        :items="ourConditionsItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="your-tasks"
  >
    <ContentCard
      :heading="t('jc.view.graphicArtist.yourTasks.title')"
      :paragraph="t('jc.view.graphicArtist.yourTasks.text')"
    >
      <IconList
        :items="yourTasksItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="impressions"
  >
    <ContentCard
      :heading="t('jc.view.graphicArtist.impressions.title')"
      :paragraph="t('jc.view.graphicArtist.impressions.text')"
    >
      <div
        class="grid grid-cols-1 laptop:grid-cols-2 gap-x-4 gap-y-8"
      >
        <template
          v-for="({ identifier, ...resource }, index) in impressionsResources"
          :key="index"
        >
          <DynamicResource
            :resource="resource"
            :caption="t(`jc.view.graphicArtist.impressions.${identifier}.text`)"
            aspect-video
            rounded
          />
        </template>
      </div>
    </ContentCard>
  </Container>

  <Container
    id="how-to-apply"
  >
    <ContentCard
      :heading="t('jc.view.graphicArtist.howToApply.title')"
      :paragraph="t('jc.view.graphicArtist.howToApply.text')"
    >
      <IconSection
        :sections="howToApplySections"
      />
    </ContentCard>
  </Container>

  <Container
    id="more-information"
  >
    <div
      class="flex flex-col gap-8"
    >
      <ContentCard
        :heading="t('jc.view.graphicArtist.moreInformation.title')"
        :paragraph="t('jc.view.graphicArtist.moreInformation.text')"
      />
      <div
        class="grid grid-cols-1 mobile:gird-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 gap-x-4 gap-y-16 justify-center"
      >
        <template
          v-for="(card, index) in moreInformationCards"
          :key="index"
        >
          <ShowcaseCard
            v-bind="card"
          />
        </template>
      </div>
    </div>
  </Container>

  <Container
    id="callToAction"
    :background="'white'"
  >
    <ParticipateCallCard />
  </Container>
</template>

<script setup lang="ts">
const banner = 'images/component/participateCallCard/background.png'
const float = 'images/skin_renders/roles/graphic_artist.png'

definePageMeta({
  nameKey: 'participate-graphicArtist',
})

const { t } = useI18n()
const localePath = useLocalePath()
const config = useRuntimeConfig()

const ourOfferHighlightItems = [
  {
    icon: 'users',
    text: t('jc.view.graphicArtist.ourOffer.iconHighlight.atmosphere.text'),
  },
  {
    icon: 'dragon',
    text: t('jc.view.graphicArtist.ourOffer.iconHighlight.creativeDevelopment.text'),
  },
  {
    icon: 'flask-round-potion',
    text: t('jc.view.graphicArtist.ourOffer.iconHighlight.innovativeConcepts.text'),
  },
  {
    icon: 'brain-circuit',
    text: t('jc.view.graphicArtist.ourOffer.iconHighlight.newTechniques.text'),
  },
  {
    icon: 'file-certificate',
    text: t('jc.view.graphicArtist.ourOffer.iconHighlight.licences.text'),
  },
  {
    icon: 'campground',
    text: t('jc.view.graphicArtist.ourOffer.iconHighlight.teamEvents.text'),
  },
]

const ourOfferItems = [
  {
    icon: 'shield-check',
    text: t('jc.view.graphicArtist.ourOffer.icon.securities.text'),
  },
  {
    icon: 'brain-arrow-curved-right',
    text: t('jc.view.graphicArtist.ourOffer.icon.coDecide.text'),
  },
  {
    icon: 'sitemap',
    text: t('jc.view.graphicArtist.ourOffer.icon.hierarchy.text'),
  },
  {
    icon: 'clock',
    text: t('jc.view.graphicArtist.ourOffer.icon.timeManagement.text'),
  },
  {
    icon: 'toolbox',
    text: t('jc.view.graphicArtist.ourOffer.icon.teamTools.text'),
  },
  {
    icon: 'image-portrait',
    text: t('jc.view.graphicArtist.ourOffer.icon.portfolio.text'),
  },
  {
    icon: 'gears',
    text: t('jc.view.graphicArtist.ourOffer.icon.supportedWorkflows.text'),
  },
  {
    icon: 'fingerprint',
    text: t('jc.view.graphicArtist.ourOffer.icon.corporateIdentity.text'),
  },
  {
    icon: 'cubes',
    text: t('jc.view.graphicArtist.ourOffer.icon.resourcepack.text'),
  },
  {
    icon: 'comments',
    text: t('jc.view.graphicArtist.ourOffer.icon.exchange.text'),
  },
]

const ourConditionsItems = [
  {
    icon: 'couch',
    text: t('jc.view.graphicArtist.ourConditions.icon.teamSpirit.text'),
  },
  {
    icon: 'alicorn',
    text: t('jc.view.graphicArtist.ourConditions.icon.fantasy.text'),
  },
  {
    icon: 'book-open-reader',
    text: t('jc.view.graphicArtist.ourConditions.icon.learn.text'),
  },
  {
    icon: 'gauge-max',
    text: t('jc.view.graphicArtist.ourConditions.icon.motivation.text'),
  },
  {
    icon: 'comment-smile',
    text: t('jc.view.graphicArtist.ourConditions.icon.communication.text'),
  },
  {
    icon: 'battery-full',
    text: t('jc.view.graphicArtist.ourConditions.icon.endurance.text'),
  },
]

const yourTasksItems = [
  {
    icon: 'sparkles',
    text: t('jc.view.graphicArtist.yourTasks.icon.edit.text'),
  },
  {
    icon: 'paintbrush',
    text: t('jc.view.graphicArtist.yourTasks.icon.texture.text'),
  },
  {
    icon: 'cubes',
    text: t('jc.view.graphicArtist.yourTasks.icon.model.text'),
  },
  {
    icon: 'palette',
    text: t('jc.view.graphicArtist.yourTasks.icon.design.text'),
  },
  {
    icon: 'hand-holding-magic',
    text: t('jc.view.graphicArtist.yourTasks.icon.give.text'),
  },
  {
    icon: 'images',
    text: t('jc.view.graphicArtist.yourTasks.icon.compose.text'),
  },
]

const impressionsResources = [
  {
    identifier: 'hub',
    mediaType: 'image/*',
    src: 'images/impressions/general/hub.png',
  },
  {
    identifier: 'clashlandsHub',
    mediaType: 'image/*',
    src: 'images/impressions/general/pvp_hub.png',
  },
  {
    identifier: 'dungeonRoom',
    mediaType: 'image/*',
    src: 'images/impressions/general/dungeon_room.png',
  },
  {
    identifier: 'conquestMap',
    mediaType: 'image/*',
    src: 'images/impressions/general/conquest_map.png',
  },
  {
    identifier: 'cosmeticMenu',
    mediaType: 'image/*',
    src: 'images/impressions/general/cosmetic_menu.png',
  },
  {
    identifier: 'clashlandsCannon',
    mediaType: 'image/*',
    src: 'images/impressions/general/cannon.png',
  },
  {
    identifier: 'aseprite',
    mediaType: 'image/*',
    src: 'images/impressions/graphic_artist/aseprite.png',
  },
  {
    identifier: 'photoshop',
    mediaType: 'image/*',
    src: 'images/impressions/graphic_artist/photoshop.png',
  },
]

const howToApplySections = [
  {
    icon: 'hand-wave',
    title: t('jc.view.graphicArtist.howToApply.icon.firstContact.title'),
    text: t('jc.view.graphicArtist.howToApply.icon.firstContact.text'),
  },
  {
    icon: 'comments',
    title: t('jc.view.graphicArtist.howToApply.icon.conversation.title'),
    text: t('jc.view.graphicArtist.howToApply.icon.conversation.text'),
  },
  {
    icon: 'envelope',
    title: t('jc.view.graphicArtist.howToApply.icon.wait.title'),
    text: t('jc.view.graphicArtist.howToApply.icon.wait.text'),
  },
  {
    icon: 'user-graduate',
    title: t('jc.view.graphicArtist.howToApply.icon.trainee.title'),
    text: t('jc.view.graphicArtist.howToApply.icon.trainee.text'),
  },
  {
    icon: 'comment-smile',
    title: t('jc.view.graphicArtist.howToApply.icon.feedbackTalk.title'),
    text: t('jc.view.graphicArtist.howToApply.icon.feedbackTalk.text'),
  },
]

const moreInformationCards = [
  {
    icon: 'users',
    text: t('jc.view.graphicArtist.moreInformation.card.team.text'),
    label: t('jc.view.graphicArtist.moreInformation.card.team.button.label'),
    to: localePath({ name: 'team' }),
  },
  {
    icon: 'newspaper',
    text: t('jc.view.graphicArtist.moreInformation.card.weekly_review.text'),
    label: t('jc.view.graphicArtist.moreInformation.card.weekly_review.button.label'),
    to: localePath({ name: 'news' }),
  },
  {
    icon: 'signs-post',
    text: t('jc.view.graphicArtist.moreInformation.card.plan.text'),
    label: t('jc.view.graphicArtist.moreInformation.card.plan.button.label'),
    to: localePath({ name: 'game' }),
  },
  {
    icon: 'microchip',
    text: t('jc.view.graphicArtist.moreInformation.card.stackShare.text'),
    label: t('jc.view.graphicArtist.moreInformation.card.stackShare.button.label'),
    to: config.public.stackShareUrl,
  },
  {
    icon: 'cube',
    text: t('jc.view.graphicArtist.moreInformation.card.planetMinecraft.text'),
    label: t('jc.view.graphicArtist.moreInformation.card.planetMinecraft.button.label'),
    to: config.public.planetMinecraftUrl,
  },
  {
    icon: 'file-lines',
    text: t('jc.view.graphicArtist.moreInformation.card.blog.text'),
    label: t('jc.view.graphicArtist.moreInformation.card.blog.button.label'),
    to: config.public.scrayosUrl,
  },
]
</script>
